import {
  resolveAxis,
  applyResistance,
  clampDrag,
  hasReachedCommit,
  normalizeWheelDelta,
  commitDuration,
  cancelDuration,
  SLOP
} from './swipe-math.js';
import { createMobileEditor } from '../editor/codemirror-setup.js';

// The page locks in the moment it passes COMMIT_RATIO of the screen and then
// completes on its own. Two consequences shape everything below:
//   - The travel left to animate is always the same (1 - COMMIT_RATIO of the
//     width), so a fixed duration is exactly right — the usual objection to
//     fixed durations, that they are wrong for a variable distance, does not
//     apply here.
//   - A page in flight is never interrupted, so a swipe can never carry past
//     its neighbour into the note after it.
// A CSS transition rather than a JS-driven tween: it runs on the compositor, so
// it stays smooth even while CodeMirror is busy on the main thread.
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'; // iOS sheet curve — front-loaded, no bounce

const WHEEL_IDLE_MS = 90;      // no wheel event for this long => gesture ended
const COOLDOWN_MS = 120;       // swallow the momentum tail of a consumed flick
const MOMENTUM_BREAK_RATIO = 1.6; // a delta this much bigger than the decaying tail is new input
const MOMENTUM_BREAK_MIN = 10; // px — below this, never treat a spike as new input
const MOMENTUM_RESET_MS = 200; // silence this long means the next event starts fresh

const idle = typeof requestIdleCallback === 'function'
  ? (fn) => requestIdleCallback(fn, { timeout: 200 })
  : (fn) => setTimeout(fn, 0);

// Horizontal pager for the note deck. Touch and trackpad feed one shared
// offset, so both produce identical motion — the whole point of the module.
export function attachDeckGestures(container, { getIndex, getPageCount, getNoteAt, onCommit }) {
  const slideEl = container.querySelector('.deck-slide');
  if (!slideEl) return () => {};

  let ghostEl = null;
  let ghostView = null;
  let ghostIndex = null;   // which page the ghost currently holds
  let ghostText = null;    // and its exact text, so an edited note isn't served stale

  let x = 0;          // painted offset, px
  let rawDx = 0;      // accumulated input, before resistance and clamping
  let gestureBase = 0; // offset this gesture started from (non-zero when carried over)
  let width = 0;
  let ghostSide = 0;  // +1 = neighbour on the right, -1 = on the left, 0 = none
  let active = false; // finger or trackpad is currently driving

  let animating = false;
  let animCommit = false;
  let animIndex = 0;
  let animEl = null;
  let animTimer = null;
  let lastCommitAt = -Infinity;

  // Input that arrived while a page was in flight, replayed the instant it lands
  // so that swiping faster than the animation loses nothing.
  let carryPending = false;
  let carryDx = 0;
  let carryFromTouch = false;

  // touch bookkeeping
  let touching = false;
  let axis = null;
  let startX = 0;
  let startY = 0;
  let lockX = 0;

  // wheel bookkeeping
  let wheelIdleTimer = null;
  let cooldownUntil = 0;
  let lastAbsDelta = 0;
  let lastWheelAt = 0;

  // Measured lazily and invalidated on resize rather than read per gesture:
  // clientWidth forces a layout, and a fast swiper starts a lot of gestures.
  let cachedWidth = 0;
  function pageWidth() {
    if (!cachedWidth) cachedWidth = container.clientWidth;
    return cachedWidth;
  }
  function invalidateWidth() { cachedWidth = 0; }

  function now() { return performance.now(); }

  // --- painting ------------------------------------------------------------

  // Style writes are deduped: during a drag paint() runs per input event, and
  // the stationary layer would otherwise be assigned an identical transform
  // every frame, invalidating style for nothing.
  let lastSlideTransform = null;
  let lastGhostTransform = null;

  function setSlideTransform(value) {
    if (value === lastSlideTransform) return;
    lastSlideTransform = value;
    slideEl.style.transform = value;
  }

  function setGhostTransform(value) {
    if (!ghostEl || value === lastGhostTransform) return;
    lastGhostTransform = value;
    ghostEl.style.transform = value;
  }

  // Drawer motion, not a filmstrip: exactly one layer moves at a time and it
  // travels over a stationary one, so the deck reads as a stack of cards rather
  // than a strip scrolling past a window.
  function paint() {
    if (ghostSide === FORWARD_SIDE) {
      // Advancing: the incoming note is the drawer, sliding in over the
      // current note, which does not move at all.
      setSlideTransform('translate3d(0, 0, 0)');
      setGhostTransform(`translate3d(${-width + x}px, 0, 0)`);
    } else if (ghostSide !== 0) {
      // Going back: the current note is the drawer, sliding away to uncover
      // the previous note resting underneath it.
      setSlideTransform(`translate3d(${x}px, 0, 0)`);
      setGhostTransform('translate3d(0, 0, 0)');
    } else {
      // Rubber-banding at an end, with nothing to reveal.
      setSlideTransform(`translate3d(${x}px, 0, 0)`);
    }
  }

  // --- ghost ---------------------------------------------------------------

  function ensureGhost() {
    if (ghostView) return;
    ghostEl = document.createElement('div');
    ghostEl.className = 'deck-ghost';
    const wrapper = document.createElement('div');
    wrapper.className = 'editor-wrapper';
    ghostEl.appendChild(wrapper);
    container.appendChild(ghostEl);
    ghostView = createMobileEditor(wrapper, '', { readOnly: true });
    lastGhostTransform = null;
  }

  // Selection matters even in a non-focused view: the markdown plugin adds
  // cm-cursor-line at the selection, which un-hides that line's markers. The
  // real editor lands at the end of the doc after a switch (see app.js
  // loadActiveNoteContent), so the ghost must too or the marker state visibly
  // changes at the swap.
  //
  // Skipped entirely when the ghost already holds this exact text. Re-parsing a
  // document is the single most expensive thing a gesture can trigger, and it
  // would otherwise happen at the moment the user most wants responsiveness.
  function setGhostContent(index) {
    const note = getNoteAt(index);
    const text = note ? note.content || '' : '';
    if (ghostIndex === index && ghostText === text) return;
    ghostIndex = index;
    ghostText = text;
    ghostView.dispatch({
      changes: { from: 0, to: ghostView.state.doc.length, insert: text },
      selection: { anchor: text.length }
    });
  }

  // After a page lands, quietly load whichever note the user is most likely to
  // reach for next — the one further in the direction they were already going.
  // Done on idle so the next gesture starts with the parse already paid for.
  function prefetch(side) {
    idle(() => {
      if (active || animating || !ghostView) return;
      const index = getIndex() - side;
      if (index < 0 || index >= getPageCount()) return;
      setGhostContent(index);
    });
  }

  function showGhost() { if (ghostEl) ghostEl.classList.add('visible'); }
  function hideGhost() { if (ghostEl) ghostEl.classList.remove('visible'); }

  // The deck is mirrored: page 0 (the blank composer) sits at the RIGHT end and
  // the index increases leftward. So advancing pulls the next note in from the
  // left, and going back sends the current note off to the left. ghostSide
  // describes which screen side the neighbour occupies (-1 left, +1 right); the
  // index it maps to is the opposite sign.
  const FORWARD_SIDE = -1;

  function indexFor(side) { return getIndex() - side; }

  // Whichever layer is the drawer — the one that actually moves.
  function movingEl() {
    return ghostSide === FORWARD_SIDE ? ghostEl : slideEl;
  }

  function atEdgeFor(value) {
    const index = getIndex();
    // Mirrored deck: dragging right advances (index + 1), dragging left goes back.
    if (value > 0) return index >= getPageCount() - 1;
    if (value < 0) return index <= 0;
    return false;
  }

  // The drawer gets the edge shadow, and the ghost drops beneath the slide when
  // it is being uncovered rather than laid over.
  function applyDrawerChrome() {
    if (!ghostEl) return;
    ghostEl.classList.toggle('under', ghostSide !== FORWARD_SIDE);
    ghostEl.classList.toggle('deck-drawer-edge', ghostSide === FORWARD_SIDE);
    slideEl.classList.toggle('deck-drawer-edge', ghostSide !== FORWARD_SIDE);
  }

  function clearDrawerChrome() {
    if (ghostEl) ghostEl.classList.remove('deck-drawer-edge', 'under');
    slideEl.classList.remove('deck-drawer-edge');
  }

  function syncGhost(edge) {
    const side = x < 0 ? 1 : x > 0 ? -1 : 0;
    if (edge || side === 0) {
      if (ghostSide !== 0) clearDrawerChrome();
      ghostSide = 0;
      hideGhost();
      return;
    }
    if (side !== ghostSide) {
      ghostSide = side;
      ensureGhost();
      setGhostContent(indexFor(side));
      applyDrawerChrome();
      showGhost();
    }
  }

  function setRawDx(value) {
    rawDx = value;
    const edge = atEdgeFor(rawDx);
    x = clampDrag(applyResistance(rawDx, edge), width, edge);
    syncGhost(edge);
    paint();

    // Past the threshold the outcome is settled — take the gesture away from
    // the user and finish it, rather than letting them drag on toward the note
    // after this one.
    if (!edge && ghostSide !== 0 && hasReachedCommit(x, width)) lockIn();
  }

  function beginGesture(carry = 0) {
    active = true;
    width = pageWidth();
    gestureBase = carry;
    rawDx = 0;
    x = 0;
    ghostSide = 0;
    slideEl.style.willChange = 'transform';
    if (ghostEl) ghostEl.style.willChange = 'transform';
  }

  function endInput() {
    active = false;
    touching = false;
    axis = null;
    clearTimeout(wheelIdleTimer);
    wheelIdleTimer = null;
  }

  function lockIn() {
    const target = indexFor(ghostSide);
    if (target < 0 || target >= getPageCount()) return;
    const wasTouch = touching;
    endInput();
    touching = wasTouch; // the finger may still be down; keep tracking it for carry
    startAnimation(true, target);
  }

  // --- animation -----------------------------------------------------------

  function startAnimation(commit, targetIndex) {
    const el = movingEl();
    const targetX = commit ? -ghostSide * width : 0;
    const duration = commit
      ? commitDuration(now() - lastCommitAt)
      : cancelDuration(x, width);

    animating = true;
    animCommit = commit;
    animIndex = targetIndex;
    animEl = el;

    if (el) {
      // Flush the current offset as the transition's start value. Without the
      // reflow the browser can collapse both writes into one style change and
      // jump straight to the target with no animation at all.
      el.style.transition = 'none';
      paint();
      void el.offsetWidth;
      el.style.transition = `transform ${duration}ms ${EASE}`;
      el.addEventListener('transitionend', onTransitionEnd);
    }

    x = targetX;
    paint();

    clearTimeout(animTimer);
    animTimer = setTimeout(finishAnimation, duration + 60);
  }

  function onTransitionEnd(e) {
    // Guarded: CodeMirror's own transitions bubble up through this element, and
    // an unguarded handler would end the page early on one of them.
    if (e.target !== animEl || e.propertyName !== 'transform') return;
    finishAnimation();
  }

  function finishAnimation() {
    if (!animating) return;
    animating = false;
    clearTimeout(animTimer);

    if (animEl) {
      animEl.removeEventListener('transitionend', onTransitionEnd);
      animEl.style.transition = 'none';
    }
    animEl = null;

    const commit = animCommit;
    const targetIndex = animIndex;
    const side = ghostSide;
    animCommit = false;

    // Order matters: swap the real editor while the drawer still covers it,
    // reset the layers underneath that cover, and only then drop the ghost.
    if (commit && targetIndex >= 0 && targetIndex < getPageCount()) {
      onCommit(targetIndex);
      lastCommitAt = now();
      // The ghost's content is now the live editor's content, so what the ghost
      // holds no longer matches the page it claims to.
      ghostIndex = null;
      ghostText = null;
    }

    x = 0;
    rawDx = 0;
    slideEl.style.transition = 'none';
    lastSlideTransform = null;
    setSlideTransform('translate3d(0, 0, 0)');
    if (ghostEl) ghostEl.style.transition = 'none';

    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (active || animating) return; // a new gesture already took over
      hideGhost();
      clearDrawerChrome();
      ghostSide = 0;
      if (ghostEl) {
        lastGhostTransform = null;
        ghostEl.style.transform = '';
        ghostEl.style.transition = '';
        ghostEl.style.willChange = '';
      }
      slideEl.style.transition = '';
      slideEl.style.willChange = '';
    }));

    cooldownUntil = now() + COOLDOWN_MS;

    const replayed = carryPending ? replayCarry() : false;
    if (!replayed && commit && side !== 0) prefetch(side);
  }

  // Input that arrived mid-flight is applied the moment the page lands. If it
  // already amounts to a committing drag it locks in immediately, so someone
  // swiping faster than the animation chains pages instead of losing swipes.
  function replayCarry() {
    const carry = carryDx;
    const fromTouch = carryFromTouch;
    carryPending = false;
    carryDx = 0;
    carryFromTouch = false;

    // Not worth starting a gesture for: it would only animate straight back.
    if (Math.abs(carry) < SLOP) return false;

    cooldownUntil = 0;
    beginGesture(fromTouch ? carry : 0);
    setRawDx(carry);

    if (!active) return true; // locked in immediately — a chained page
    if (fromTouch) {
      touching = true;
      axis = 'h';
    } else {
      clearTimeout(wheelIdleTimer);
      wheelIdleTimer = setTimeout(endGesture, WHEEL_IDLE_MS);
    }
    return true;
  }

  function accumulateCarry(delta, fromTouch) {
    carryPending = true;
    carryFromTouch = fromTouch;
    carryDx = clampDrag(carryDx + delta, pageWidth(), false);
  }

  // Released before the threshold: the page always returns. There is no
  // velocity escape hatch, so a swipe either passed 20% and completed itself or
  // it goes back — never a judgement call at the moment the finger lifts.
  function endGesture() {
    if (!active) {
      endInput();
      return;
    }
    endInput();
    if (x === 0) {
      hideGhost();
      clearDrawerChrome();
      ghostSide = 0;
      slideEl.style.willChange = '';
      if (ghostEl) ghostEl.style.willChange = '';
      return;
    }
    startAnimation(false, getIndex());
  }

  // --- touch ---------------------------------------------------------------

  function onTouchStart(e) {
    if (e.touches.length !== 1) return;
    touching = true;
    axis = null;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    lockX = startX;
  }

  function onTouchMove(e) {
    if (!touching || e.touches.length !== 1) return;
    const px = e.touches[0].clientX;
    const py = e.touches[0].clientY;

    if (axis === null) {
      axis = resolveAxis(px - startX, py - startY);
      if (axis === null) return;
      if (axis === 'v') { touching = false; return; }
      // Re-zero at the lock point so the drawer doesn't jump by the slop.
      lockX = px;
      if (!animating) beginGesture();
    }

    e.preventDefault();

    if (animating || !active) {
      // A page is completing. Bank the movement rather than dropping it.
      accumulateCarry(px - lockX, true);
      lockX = px;
      return;
    }

    setRawDx(gestureBase + (px - lockX));
  }

  function onTouchEnd() {
    if (carryPending) carryFromTouch = false; // the finger is gone; replay as a completed drag
    if (!touching && !active) return;
    endGesture();
  }

  // --- wheel (trackpad) ----------------------------------------------------

  // A horizontally-scrollable descendant that isn't at its edge owns the
  // gesture — scrolling a wide table sideways must not page the deck. Only
  // consulted when a gesture starts: once the pager owns a gesture it keeps it,
  // and getComputedStyle per wheel event would force a style recalc every frame.
  function ownedByScrollableX(target, deltaX) {
    let el = target instanceof Element ? target : null;
    while (el && el !== container) {
      if (el.scrollWidth > el.clientWidth + 1) {
        const overflowX = getComputedStyle(el).overflowX;
        if (overflowX === 'auto' || overflowX === 'scroll') {
          const atStart = el.scrollLeft <= 0;
          const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
          if (deltaX > 0 ? !atEnd : !atStart) return true;
        }
      }
      el = el.parentElement;
    }
    return false;
  }

  function onWheel(e) {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical intent

    // Unconditional for horizontal wheels: this is what stops Chrome and
    // Safari turning the swipe into browser back/forward.
    e.preventDefault();

    const t = now();
    const abs = Math.abs(e.deltaX);
    if (t - lastWheelAt > MOMENTUM_RESET_MS) lastAbsDelta = 0;
    lastWheelAt = t;
    const prevAbs = lastAbsDelta;
    lastAbsDelta = abs;

    const delta = -normalizeWheelDelta(e.deltaX, e.deltaMode, pageWidth());

    // A page in flight is never interrupted, so a long flick cannot carry on
    // past its neighbour into the note after it. Genuinely new input is banked
    // and replayed the moment it lands.
    if (animating) {
      const isNewInput = carryPending
        || (abs > prevAbs * MOMENTUM_BREAK_RATIO && abs > MOMENTUM_BREAK_MIN);
      if (isNewInput) accumulateCarry(delta, false);
      return;
    }

    if (!active) {
      if (t < cooldownUntil) {
        // macOS keeps delivering a decaying momentum tail after the fingers
        // lift. Only a delta that rises sharply above that tail is a genuinely
        // new swipe; anything still decaying is the flick already consumed.
        if (!(abs > prevAbs * MOMENTUM_BREAK_RATIO && abs > MOMENTUM_BREAK_MIN)) {
          cooldownUntil = Math.max(cooldownUntil, t + COOLDOWN_MS);
          return;
        }
        cooldownUntil = 0;
      } else if (ownedByScrollableX(e.target, e.deltaX)) {
        return;
      }
      beginGesture();
    }

    setRawDx(rawDx + delta);

    if (!active) return; // lockIn() finished the gesture inside setRawDx
    clearTimeout(wheelIdleTimer);
    wheelIdleTimer = setTimeout(endGesture, WHEEL_IDLE_MS);
  }

  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchmove', onTouchMove, { passive: false });
  container.addEventListener('touchend', onTouchEnd, { passive: true });
  container.addEventListener('touchcancel', onTouchEnd, { passive: true });
  container.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('resize', invalidateWidth);
  window.addEventListener('orientationchange', invalidateWidth);

  return function detach() {
    container.removeEventListener('touchstart', onTouchStart);
    container.removeEventListener('touchmove', onTouchMove);
    container.removeEventListener('touchend', onTouchEnd);
    container.removeEventListener('touchcancel', onTouchEnd);
    container.removeEventListener('wheel', onWheel);
    window.removeEventListener('resize', invalidateWidth);
    window.removeEventListener('orientationchange', invalidateWidth);
    clearTimeout(wheelIdleTimer);
    clearTimeout(animTimer);
    if (animEl) animEl.removeEventListener('transitionend', onTransitionEnd);
    if (ghostView) ghostView.destroy();
    if (ghostEl) ghostEl.remove();
    ghostView = null;
    ghostEl = null;
  };
}
